import { useState, useCallback } from 'react';
import { isEmpty } from '../utility';
import alerts from 'constants/alerts';

/**
 * Advanced validation hook that handles all validation patterns:
 * 1. Validation errors don't auto-hide
 * 2. Real-time validation updates on field changes
 * 3. Validation only starts after first submit click
 * 4. Unified snackbar for validation and API responses
 * 5. Optimized and reusable across all screens
 */
export const useAdvancedValidation = (fieldConfig) => {
  // Field configuration - defines validation rules for each field
  const FIELD_CONFIG = fieldConfig || {};
  const FIELD_KEYS = Object.keys(FIELD_CONFIG);

  // Validation state management
  const [validationTriggered, setValidationTriggered] = useState(false);
  const [errorState, setErrorState] = useState({
    message: "",
    show: false,
    fieldErrors: FIELD_KEYS.reduce((acc, key) => ({ ...acc, [key]: false }), {}),
    fieldMessages: FIELD_KEYS.reduce((acc, key) => ({ ...acc, [key]: "" }), {})
  });

  // Unified snackbar state for validation and API errors
  const [snackbarState, setSnackbarState] = useState({
    message: "",
    show: false,
    type: "error", // "error" or "success"
    autoHideDuration: 0, // 0 for validation errors, 3000 for API responses
  });

  // Error management helpers
  const clearError = useCallback((fieldName) => {
    if (fieldName) {
      setErrorState((prev) => ({
        ...prev,
        fieldErrors: {
          ...prev.fieldErrors,
          [fieldName]: false,
        },
        fieldMessages: {
          ...prev.fieldMessages,
          [fieldName]: "",
        },
      }));
    } else {
      setErrorState({
        message: "",
        show: false,
        fieldErrors: FIELD_KEYS.reduce((acc, key) => ({ ...acc, [key]: false }), {}),
        fieldMessages: FIELD_KEYS.reduce((acc, key) => ({ ...acc, [key]: "" }), {})
      });
    }
  }, [FIELD_KEYS]);

  // Snackbar dismiss function
  const dismissSnackbar = useCallback(() => {
    setSnackbarState(prev => ({
      ...prev,
      show: false
    }));
  }, []);

  // API response handler for unified snackbar
  const handleApiResponse = useCallback((message, type = "error") => {
    setSnackbarState({
      message: message,
      show: true,
      type: type,
      autoHideDuration: 3000
    });
  }, []);

  // Helper function for real-time validation
  const handleFieldValidation = useCallback((fieldName, value) => {
    if (validationTriggered) {
      const fieldConfig = FIELD_CONFIG[fieldName];
      if (!fieldConfig) return;

      let isValid = false;
      let errorMessage = "";

      // Simplified validation logic - only check if required field is empty
      if (fieldConfig.validation?.required) {
        isValid = !isEmpty(value);
        errorMessage = isValid ? "" : "Incorrect or Missing Details";
      } else {
        // Default validation - just check if not empty
        isValid = !isEmpty(value);
        errorMessage = isValid ? "" : "Incorrect or Missing Details";
      }

      // Special validation for zip_code length
      if (fieldName === 'zip_code' && !isEmpty(value) && fieldConfig.validation?.length) {
        const zipValue = typeof value === 'string' || typeof value === 'number' ? `${value}`.trim() : '';
        if (zipValue.length !== fieldConfig.validation.length) {
          isValid = false;
          errorMessage = alerts?.zipcode_length_error;
        }
      }

      // Update field error state
      setErrorState((prev) => ({
        ...prev,
        fieldErrors: { ...prev.fieldErrors, [fieldName]: !isValid },
        fieldMessages: { ...prev.fieldMessages, [fieldName]: errorMessage },
      }));

      // Update snackbar visibility based on all field errors
      const updatedErrors = { ...errorState.fieldErrors, [fieldName]: !isValid };
      const hasAnyErrors = Object.values(updatedErrors).some(error => error);

      if (hasAnyErrors) {
        // Show snackbar if there are any errors (no auto-hide for validation errors)
        setSnackbarState({
          message: "Incorrect or Missing Details Below",
          show: true,
          type: "error",
          autoHideDuration: 0,
        });
      } else {
        // Hide snackbar if no errors
        dismissSnackbar();
      }
    } else {
      // Clear field error when user starts typing (before validation triggered)
      if (errorState.fieldErrors?.[fieldName]) {
        clearError(fieldName);
      }
      if (snackbarState.show) {
        dismissSnackbar();
      }
    }
  }, [validationTriggered, FIELD_CONFIG, errorState.fieldErrors, snackbarState.show, dismissSnackbar, clearError]);

  // Validate all fields function
  const validateAllFields = useCallback((formData) => {
    // Set validation triggered to true (enables real-time validation)
    setValidationTriggered(true);

    // Clear previous errors
    clearError();

    // Validate all fields using if conditions (not if-else)
    let hasErrors = false;
    const fieldErrors = FIELD_KEYS.reduce((acc, key) => ({ ...acc, [key]: false }), {});
    const fieldMessages = FIELD_KEYS.reduce((acc, key) => ({ ...acc, [key]: "" }), {});
    let errorMessage = ""
    FIELD_KEYS.forEach(fieldName => {
      const fieldConfig = FIELD_CONFIG[fieldName];
      const value = formData[fieldName];
      
      // Simplified validation - only check if required field is empty
      if (fieldConfig.validation?.required && isEmpty(value)) {
        fieldErrors[fieldName] = true;
        fieldMessages[fieldName] = "Incorrect or Missing Details";
        errorMessage = fieldConfig?.message
        hasErrors = true;
      } else if (!fieldConfig.validation?.required && isEmpty(value)) {
        fieldErrors[fieldName] = true;
        fieldMessages[fieldName] = "Incorrect or Missing Details";
        errorMessage = fieldConfig?.message
        hasErrors = true;
      }

      // Special validation for zip_code length
      if (fieldName === 'zip_code' && !isEmpty(value) && fieldConfig.validation?.length) {
        const zipValue = typeof value === 'string' || typeof value === 'number' ? `${value}`.trim() : '';
        if (zipValue.length !== fieldConfig.validation.length) {
          fieldErrors[fieldName] = true;
          fieldMessages[fieldName] = alerts?.zipcode_length_error;
          errorMessage = alerts?.zipcode_length_error;
          hasErrors = true;
        }
      }
    });

    // Set all errors at once if any validation failed
    if (hasErrors) {
      setErrorState({
        message: errorMessage || "Incorrect or Missing Details Below",
        show: true,
        fieldErrors,
        fieldMessages,
      });

      // Show unified snackbar for validation errors (no auto-hide)
      setSnackbarState({
        message: errorMessage || "Incorrect or Missing Details Below",
        show: true,
        type: "error",
        autoHideDuration: 0,
      });
      return false;
    }

    return true;
  }, [FIELD_CONFIG, FIELD_KEYS, clearError]);

  // Helper function to generate field props
  const getFieldProps = useCallback((fieldName) => ({
    error: validationTriggered && errorState.fieldErrors?.[fieldName],
    errorMessage: validationTriggered && errorState.fieldErrors?.[fieldName] ? errorState.fieldMessages?.[fieldName] : "",
    placeholderTextColor: validationTriggered && errorState.fieldErrors?.[fieldName] ? "#FF6B6B" : "#999999" // Primary color for errors
  }), [validationTriggered, errorState.fieldErrors, errorState.fieldMessages]);

  // Check if form has any errors
  const hasFieldErrors = Object.values(errorState.fieldErrors).some(error => error);

  // Create onApiResponse handler for store actions
  const onApiResponse = createOnApiResponseHandler(setSnackbarState);

  return {
    // State
    validationTriggered,
    errorState,
    snackbarState,
    
    // Functions
    handleFieldValidation,
    validateAllFields,
    clearError,
    dismissSnackbar,
    handleApiResponse,
    getFieldProps,
    onApiResponse, // Standardized callback for store actions
    
    // Computed values
    hasFieldErrors,
    
    // Field configuration
    FIELD_CONFIG,
    FIELD_KEYS
  };
};

/**
 * Standardized onApiResponse callback handler for MessageSnackBar
 * This function should be passed to store actions as onApiResponse callback
 * @param {Function} setSnackbarState - Function to update snackbar state (from useAdvancedValidation hook)
 * @returns {Function} onApiResponse callback function
 */
export const createOnApiResponseHandler = (setSnackbarState) => {
  return (message, type = "error", customAutoHideDuration) => {
    if (setSnackbarState) {
      setSnackbarState({
        message: message,
        show: true,
        type: type,
        autoHideDuration: customAutoHideDuration !== undefined ? customAutoHideDuration : 3000 // Default 3s, can override with 0 for manual dismiss
      });
    }
  };
};

